import { html, LitElement, css, PropertyDeclarations, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { getGlobalStyleSheets } from '../../global-styles';

import * as Tags from './user-menus.tagnames';
import { AtlUserProfileMenuItemClickEvent } from './user-menus.events';
import { ButtonEnums } from '../button';
import { enumAttributeConverter } from '../enum-types';
import { JustifyContent } from '../justify-content';

@customElement(Tags.UserProfileMenuItem)
export class AtlUserProfileMenuItem extends LitElement {
   declare justifyContent?: JustifyContent;
   declare variant?: ButtonEnums.ButtonVariant;
   declare backgroundColor?: string;
   declare fullWidth?: boolean;
   declare size?: ButtonEnums.ButtonSize;

   static properties: PropertyDeclarations = {
      justifyContent: {
         attribute: 'justify-content',
         type: String,
         reflect: true,
         converter: enumAttributeConverter(
            JustifyContent,
            JustifyContent.Center
         ),
      },
      variant: {
         type: String,
         converter: enumAttributeConverter(
            ButtonEnums.ButtonVariant,
            ButtonEnums.ButtonVariant.Action
         ),
      },
      backgroundColor: {
         attribute: 'background-color',
         type: String,
      },
      fullWidth: {
         attribute: 'full-width',
         converter: {
            fromAttribute: (value: string | null) =>
               value === null ? true : value.toLowerCase() !== 'false',
         },
      },
      size: {
         type: String,
         converter: enumAttributeConverter(ButtonEnums.ButtonSize),
      },
   };

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         :host {
            display: block;
         }
         :host(:hover),
         :host(:focus-within) {
            background-color: transparent !important;
         }
         atlantis-button {
            display: block;
         }
         atlantis-button::part(button) {
            text-decoration: none;
         }
         atlantis-button::part(button):hover {
            text-decoration: none;
         }
         .variant-action atlantis-button::part(button) {
            color: var(--tti-user-profile-menu-text-color, #000);
         }
         .variant-action atlantis-button::part(button):hover {
            color: var(--tti-user-profile-menu-text-color, #000);
         }
         .has-bg atlantis-button::part(button) {
            color: #fff !important;
            background-color: var(--_bg-color);
         }
         .has-bg atlantis-button::part(button):hover {
            color: #fff !important;
            filter: brightness(0.9);
         }
         div:not(.has-bg) atlantis-button::part(button):hover {
            background-color: var(
               --tti-user-profile-menu-item-hover-background-color,
               #f0f0f0
            );
         }
      `,
   ];

   render() {
      const isFullWidth = this.fullWidth !== undefined ? this.fullWidth : true;
      const hasBg = !!this.backgroundColor;
      const resolvedVariant = this.variant ?? ButtonEnums.ButtonVariant.Action;
      const isAction = resolvedVariant === ButtonEnums.ButtonVariant.Action;

      return html`
         <div
            class=${classMap({ 'has-bg': hasBg, 'variant-action': isAction })}
            style=${hasBg
               ? styleMap({ '--_bg-color': this.backgroundColor! })
               : nothing}
         >
            <atlantis-button
               exportparts="button"
               .fullWidth=${isFullWidth}
               .variant=${resolvedVariant}
               .size=${this.size}
               .justifyContent=${this.justifyContent ?? JustifyContent.Center}
               @click=${() =>
                  this.dispatchEvent(new AtlUserProfileMenuItemClickEvent())}
            >
               <span><slot></slot></span>
            </atlantis-button>
         </div>
      `;
   }
}
